/**
 * 시각 표시 중앙 헬퍼.
 *
 * 모든 사용자 노출 timestamp 는 이 헬퍼를 거친다. 시간대(TZ)는 브라우저 자동 감지
 * (`Intl.DateTimeFormat().resolvedOptions().timeZone`) — 미국 사용자엔 미국 시각.
 * 포맷 locale 은 기본 'ko-KR'. 향후 사용자 설정 (mopeers.locale) 합류 시 여기 한 곳만
 * 변경하면 사이트 전체에 반영됨.
 *
 * 입력은 ISO string / Date / null / undefined 모두 허용. 빈 값 → '-'.
 */

const DEFAULT_LOCALE = 'ko-KR';

let currentLocale: string = DEFAULT_LOCALE;
let currentTimezone: string | undefined = undefined;  // undefined = 브라우저 자동

/** 향후 사용자 설정에서 호출. App.tsx 의 checkAuth 후 user.locale 로 설정 권장. */
export function setUserLocale(locale: string | null | undefined): void {
 currentLocale = locale || DEFAULT_LOCALE;
}

export function getUserLocale(): string {
 return currentLocale;
}

/** 사용자 시간대 설정. null/undefined → 브라우저 자동 감지. */
export function setUserTimezone(timezone: string | null | undefined): void {
 currentTimezone = timezone || undefined;
}

export function getUserTimezone(): string | undefined {
 return currentTimezone;
}

/** opts 에 timeZone 이 명시 안 됐을 때 사용자 설정값 주입. */
function withTimezone(opts: Intl.DateTimeFormatOptions): Intl.DateTimeFormatOptions {
 if (opts.timeZone || !currentTimezone) return opts;
 return { ...opts, timeZone: currentTimezone };
}

function toDate(v: unknown): Date | null {
 if (v == null) return null;
 if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;
 if (typeof v === 'string' || typeof v === 'number') {
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
 }
 return null;
}

/** 날짜+시각 (예: '2026. 5. 4. 오후 4:30'). 사용자 시간대 (없으면 브라우저 자동). */
export function formatDateTime(
 v: unknown,
 opts: Intl.DateTimeFormatOptions = {},
): string {
 const d = toDate(v);
 if (!d) return '-';
 return d.toLocaleString(currentLocale, withTimezone(opts));
}

/** 날짜만 (예: '2026. 5. 4.'). */
export function formatDate(
 v: unknown,
 opts: Intl.DateTimeFormatOptions = {},
): string {
 const d = toDate(v);
 if (!d) return '-';
 return d.toLocaleDateString(currentLocale, withTimezone(opts));
}

/** 시각만 (예: '오후 4:30'). 기본 hour:'2-digit', minute:'2-digit'. */
export function formatTime(
 v: unknown,
 opts: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit' },
): string {
 const d = toDate(v);
 if (!d) return '-';
 return d.toLocaleTimeString(currentLocale, withTimezone(opts));
}

/** 짧은 날짜 (예: '5월 4일'). 캠페인 카드 등 좁은 공간용. */
export function formatShortDate(v: unknown): string {
 return formatDate(v, { month: 'short', day: 'numeric' });
}

/**
 * 영상 라이프타임 만료 여부. `expires_at`(ISO/Date/null)이 현재 시각 이하이면 true.
 * 만료된 영상은 playing-info 가 read-time 에 슬롯에서 제외하므로 재생되지 않는다
 * (moclient-test.ts 의 expiredHashes) — 목록에서 dimmed 표시로 이 사실을 드러낸다.
 * 빈 값(만료 미설정)은 만료 아님(false).
 */
export function isExpired(v: unknown): boolean {
 const d = toDate(v);
 return d != null && d.getTime() <= Date.now();
}

/**
 * 미래 행사 시작일까지 남은 "일수"(D-N 표시용). `event_start_at`(ISO/Date/null)의 날짜가
 * 오늘보다 미래이면 남은 날짜 수(>=1), 오늘이거나 과거이거나 없으면 null.
 * 사용자 로컬 시간대의 자정 경계로 계산(시:분 무시) → "내일 시작"=1.
 * 예: 목록에서 만료 아닌 예정 영상에 "D-3" 뱃지.
 */
export function eventDDay(v: unknown): number | null {
 const d = toDate(v);
 if (!d) return null;
 const now = new Date();
 const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
 const startOfEvent = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
 const days = Math.round((startOfEvent - startOfToday) / 86_400_000);
 return days >= 1 ? days : null;
}

// ── datetime-local 입력 헬퍼 (Dashboard·VideoBoxPanel 에 중복 정의돼 있던 것 통합) ──

const pad2 = (n: number) => String(n).padStart(2, '0');

/** ISO(또는 Date) → datetime-local 입력값(로컬, 분 단위). 빈/불량 값은 ''. */
export function isoToLocalInput(iso: string | Date | null | undefined): string {
 if (!iso) return '';
 const d = new Date(iso);
 if (isNaN(d.getTime())) return '';
 return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

/** epoch ms → datetime-local 입력값(로컬, 분 단위). */
export function toLocalInput(epochMs: number): string {
 return isoToLocalInput(new Date(epochMs));
}

/** datetime-local 입력값(로컬) → epoch ms. */
export function fromLocalInput(v: string): number {
 return new Date(v).getTime();
}
